// Template2.jsx
import React, { useEffect, useRef, useState } from "react";
import jsPDF from "jspdf";
import * as htmlToImage from "html-to-image";
import { toast } from "react-toastify";
import { FaTrash, FaPencilAlt, FaPlus } from "react-icons/fa";
import {
  BsFiletypePdf,
  BsFiletypePng,
  BsFiletypeJpg,
  BsFiletypeSvg,
} from "react-icons/bs";

/**
 * Template2 - teal two-column resume design.
 * - TailwindCSS required for styling
 * - Behavior similar style to your Template1
 */

const Template2 = ({ initialData, onSave }) => {
  const resumeRef = useRef(null);

  // Basic form structure, can be seeded from props.initialData
  const [isEdit, setIsEdit] = useState(false);
  const [imageAsset, setImageAsset] = useState({
    isLoading: false,
    imageURL: null,
  });

  const [formData, setFormData] = useState({
    fullname: "Latha",
    professionalTitle: "Graphic & Web Designer",
    contactPhone: "+91 9876543210",
    contactWebsite: "www.yourwebsite.com",
    contactAddress: "769 Prudence/Lincoln Park, MI",
    about:
      "Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s...",
  });

  const [education, setEducation] = useState([
    {
      school: "JNTU Hyderabad",
      degree: "Master Degree Graduate",
      years: "2011 - 2013",
    },
    {
      school: "Malla Reddy University",
      degree: "Bachelor Degree Graduate",
      years: "2007 - 2010",
    },
  ]);

  const [references, setReferences] = useState([
    {
      name: "Ravi",
      details:
        "2813 Shobe Lane Mancos, CO. Tel: +91 1234567890",
    },
    {
      name: "Raju",
      details:
        "219 Fairfax Drive Newark, NJ. Tel: +91 0987654321",
    },
  ]);

  const [experiences, setExperiences] = useState([
    {
      title: "Senior Web Designer",
      company: "Infosys Ltd. / Bangalore",
      period: "2020 - Present",
      description:
        "Lorem Ipsum has been the industry's standard dummy text ever since the 1500s when an unknown printer took a galley of type.",
    },
    {
      title: "Graphic Designer",
      company: "TCS Ltd. / Mumbai",
      period: "2015 - 2020",
      description:
        "Lorem Ipsum has been the industry's standard dummy text ever since the 1500s when an unknown printer took a galley of type.",
    },
    {
      title: "Marketing Manager",
      company: "Wipro Ltd. / Delhi",
      period: "2013 - 2015",
      description:
        "Lorem Ipsum has been the industry's standard dummy text ever since the 1500s when an unknown printer took a galley of type.",
    },
  ]);

  const [skills, setSkills] = useState([
    { title: "Adobe Photoshop", percent: 85 },
    { title: "Adobe Illustrator", percent: 75 },
    { title: "Microsoft Word", percent: 70 },
    { title: "HTML-5/CSS-3", percent: 80 },
  ]);

  const [languages, setLanguages] = useState([
    "English",
    "Spanish",
    "French",
    "German",
  ]);
  const [hobbies, setHobbies] = useState([
    "Reading Books",
    "Traveling",
    "Playing Chess",
  ]);

  // seed with initialData if provided
  useEffect(() => {
    if (!initialData) return;

    if (initialData.formData) {
      setFormData((p) => ({ ...p, ...initialData.formData }));
    }
    if (initialData.imageURL) {
      setImageAsset((p) => ({
        ...p,
        imageURL: initialData.imageURL,
      }));
    }
    if (initialData.education) setEducation(initialData.education);
    if (initialData.references) setReferences(initialData.references);
    if (initialData.experiences) setExperiences(initialData.experiences);
    if (initialData.skills) setSkills(initialData.skills);
    if (initialData.languages) setLanguages(initialData.languages);
    if (initialData.hobbies) setHobbies(initialData.hobbies);
  }, [initialData]);

  // ========================
  // Edit toggle
  // ========================
  const toggleEdit = () => {
    setIsEdit((s) => !s);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ========================
  // Image upload
  // ========================
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
      toast.error("Only JPG/PNG allowed");
      return;
    }
    setImageAsset((p) => ({ ...p, isLoading: true }));
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImageAsset({
        isLoading: false,
        imageURL: ev.target.result,
      });
    };
    reader.readAsDataURL(file);
  };

  const deleteImage = () =>
    setImageAsset({ isLoading: false, imageURL: null });

  // ========================
  // Experience handlers
  // ========================
  const handleExpChange = (idx, e) => {
    const { name, value } = e.target;
    const arr = [...experiences];
    arr[idx][name] = value;
    setExperiences(arr);
  };

  const addExperience = () =>
    setExperiences((s) => [
      ...s,
      {
        title: "New Position",
        company: "Company / Location",
        period: "YYYY - YYYY",
        description: "",
      },
    ]);

  const removeExperience = (idx) =>
    setExperiences((s) => s.filter((_, i) => i !== idx));

  // ========================
  // Education handlers
  // ========================
  const addEducation = () =>
    setEducation((s) => [
      ...s,
      { school: "New School", degree: "Degree", years: "YYYY - YYYY" },
    ]);

  const removeEducation = (idx) =>
    setEducation((s) => s.filter((_, i) => i !== idx));

  const handleEduChange = (idx, e) => {
    const { name, value } = e.target;
    const arr = [...education];
    arr[idx][name] = value;
    setEducation(arr);
  };

  // ========================
  // Skills handlers
  // ========================
  const addSkill = () =>
    setSkills((s) => [...s, { title: "New Skill", percent: 50 }]);

  const handleSkillChange = (idx, e) => {
    const { name, value } = e.target;
    const arr = [...skills];
    arr[idx][name] =
      name === "percent" ? Number(value) : value;
    setSkills(arr);
  };

  const removeSkill = (idx) =>
    setSkills((s) => s.filter((_, i) => i !== idx));

  // ========================
  // References handlers
  // ========================
  const handleRefChange = (idx, e) => {
    const { name, value } = e.target;
    const arr = [...references];
    arr[idx][name] = value;
    setReferences(arr);
  };

  const addReference = () =>
    setReferences((s) => [
      ...s,
      { name: "New Reference", details: "Details / Phone / Email" },
    ]);

  const removeReference = (idx) =>
    setReferences((s) => s.filter((_, i) => i !== idx));

  // ========================
  // Languages handlers
  // ========================
  const handleLanguageChange = (idx, e) => {
    const arr = [...languages];
    arr[idx] = e.target.value;
    setLanguages(arr);
  };

  const addLanguage = () =>
    setLanguages((s) => [...s, "New Language"]);

  const removeLanguage = (idx) =>
    setLanguages((s) => s.filter((_, i) => i !== idx));

  // ========================
  // Hobbies handlers
  // ========================
  const handleHobbyChange = (idx, e) => {
    const arr = [...hobbies];
    arr[idx] = e.target.value;
    setHobbies(arr);
  };

  const addHobby = () =>
    setHobbies((s) => [...s, "New Hobby"]);

  const removeHobby = (idx) =>
    setHobbies((s) => s.filter((_, i) => i !== idx));

  // ========================
  // Save handler
  // ========================
  const saveFormData = async () => {
    try {
      const payload = {
        formData,
        education,
        experiences,
        skills,
        languages,
        hobbies,
        imageURL: imageAsset.imageURL,
      };
      if (onSave) await onSave(payload);
      toast.success("Saved!");
    } catch (err) {
      console.error(err);
      toast.error("Save failed");
    }
  };

  // ========================
  // Download helpers
  // ========================
  const generatePDF = async () => {
    try {
      const element = resumeRef.current;
      if (!element) {
        toast.info("Nothing to capture");
        return;
      }

      const dataUrl = await htmlToImage.toPng(element, {
        quality: 1,
        pixelRatio: 2,
      });

      const pdf = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: "a4",
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgProps = pdf.getImageProperties(dataUrl);
      const aspectRatio = imgProps.width / imgProps.height;

      // SCALE BY HEIGHT
      const imgHeight = pdfHeight;
      const imgWidth = pdfHeight * aspectRatio;
      const marginX = (pdfWidth - imgWidth) / 2;

      pdf.addImage(
        dataUrl,
        "PNG",
        marginX,
        0,
        imgWidth,
        imgHeight
      );
      pdf.save("resume.pdf");
      toast.success("PDF saved");
    } catch (err) {
      console.error(err);
      toast.error("PDF generation failed");
    }
  };

  const generateImage = async (type = "png") => {
    const element = resumeRef.current;
    if (!element) {
      toast.info("Nothing to capture");
      return;
    }
    try {
      const fn =
        type === "jpg"
          ? htmlToImage.toJpeg
          : type === "svg"
          ? htmlToImage.toSvg
          : htmlToImage.toPng;

      const dataUrl = await fn(element, {
        quality: 1,
        pixelRatio: 2,
      });

      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `resume.${type}`;
      link.click();
      toast.success("Downloaded!");
    } catch (err) {
      console.error(err);
      toast.error("Generate failed");
    }
  };

  const generatePng = () => generateImage("png");
  const generateJpg = () => generateImage("jpg");
  const generateSvg = () => generateImage("svg");

  // ========================
  // Render
  // ========================
  return (
    <div className="w-full flex flex-col items-center gap-6 py-6">
      {/* controls */}
      <div className="w-full max-w-5xl flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <button
            onClick={toggleEdit}
            className="px-3 py-1 bg-gray-200 rounded-md flex items-center gap-2"
          >
            <FaPencilAlt />{" "}
            <span>{isEdit ? "Disable Edit" : "Edit"}</span>
          </button>

          <button
            onClick={saveFormData}
            className="px-3 py-1 bg-green-100 rounded-md"
          >
            Save
          </button>
        </div>

        <div className="flex items-center gap-3">
          <BsFiletypePdf
            className="text-2xl cursor-pointer"
            onClick={generatePDF}
          />
          <BsFiletypePng
            className="text-2xl cursor-pointer"
            onClick={generatePng}
          />
          <BsFiletypeJpg
            className="text-2xl cursor-pointer"
            onClick={generateJpg}
          />
          <BsFiletypeSvg
            className="text-2xl cursor-pointer"
            onClick={generateSvg}
          />
        </div>
      </div>

      {/* A4 resume card */}
      <div
        ref={resumeRef}
        className="bg-white shadow-lg border-4 border-teal-600"
        style={{
          width: "794px",
          height: "1122px", // A4 height @ 96dpi
        }}
      >
        <div className="flex w-full h-full">
          {/* LEFT column - teal */}
          <div className="w-5/12 bg-teal-100 p-8 relative">
            <div className="pl-6">
              <input
                name="fullname"
                value={formData.fullname}
                onChange={handleChange}
                readOnly={!isEdit}
                className="w-full text-3xl font-bold tracking-wide bg-transparent outline-none text-teal-900"
              />
              <input
                name="professionalTitle"
                value={formData.professionalTitle}
                onChange={handleChange}
                readOnly={!isEdit}
                className="w-full text-sm tracking-wider uppercase bg-transparent outline-none mt-1 text-teal-700"
              />
            </div>

            {/* avatar */}
            <div className="mt-6 flex justify-center">
              <div className="w-36 h-36 rounded-full bg-white p-2 flex items-center justify-center">
                {imageAsset.imageURL ? (
                  <div className="relative w-full h-full rounded-full overflow-hidden">
                    <img
                      src={imageAsset.imageURL}
                      alt="avatar"
                      className="w-full h-full object-cover rounded-full"
                    />
                    {isEdit && (
                      <button
                        onClick={deleteImage}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 text-xs"
                        title="Remove"
                      >
                        <FaTrash />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="w-full h-full rounded-full bg-gray-200" />
                )}
              </div>
            </div>

            {/* upload if edit */}
            {isEdit && (
              <div className="mt-4 text-center">
                <input
                  accept=".png,.jpg,.jpeg"
                  onChange={handleFileSelect}
                  type="file"
                />
              </div>
            )}

            {/* CONTACT */}
            <div className="mt-8 px-4">
              <h4 className="text-teal-900 uppercase tracking-wider font-semibold">
                Contact Me
              </h4>
              <div className="h-[1px] bg-teal-600 w-14 my-3" />
              <input
                value={formData.contactPhone}
                onChange={handleChange}
                name="contactPhone"
                readOnly={!isEdit}
                className="w-full bg-transparent outline-none text-sm"
              />
              <input
                value={formData.contactWebsite}
                onChange={handleChange}
                name="contactWebsite"
                readOnly={!isEdit}
                className="w-full bg-transparent outline-none text-sm mt-1"
              />
              <textarea
                value={formData.contactAddress}
                onChange={handleChange}
                name="contactAddress"
                readOnly={!isEdit}
                rows={2}
                className="w-full bg-transparent outline-none text-sm mt-2 resize-none"
              />
            </div>

            {/* EDUCATION */}
            <div className="mt-8 px-4">
              <h4 className="text-teal-900 uppercase tracking-wider font-semibold">
                Education
              </h4>
              <div className="h-[1px] bg-teal-600 w-14 my-3" />
              <div>
                {education.map((e, idx) => (
                  <div key={idx} className="mb-4">
                    <input
                      name="school"
                      value={e.school}
                      readOnly={!isEdit}
                      onChange={(ev) => handleEduChange(idx, ev)}
                      className="w-full font-semibold bg-transparent outline-none text-sm"
                    />
                    <input
                      name="degree"
                      value={e.degree}
                      readOnly={!isEdit}
                      onChange={(ev) => handleEduChange(idx, ev)}
                      className="w-full text-xs bg-transparent outline-none mt-1"
                    />
                    <div className="flex items-center justify-between mt-1">
                      <input
                        name="years"
                        value={e.years}
                        readOnly={!isEdit}
                        onChange={(ev) => handleEduChange(idx, ev)}
                        className="text-xs bg-transparent outline-none"
                      />
                      {isEdit && (
                        <button
                          onClick={() => removeEducation(idx)}
                          className="text-red-500 text-xs"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {isEdit && (
                  <button
                    onClick={addEducation}
                    className="flex items-center gap-2 text-sm text-teal-700"
                  >
                    <FaPlus /> Add Education
                  </button>
                )}
              </div>
            </div>

            {/* REFERENCES */}
            <div className="mt-8 px-4">
              <h4 className="text-teal-900 uppercase tracking-wider font-semibold">
                References
              </h4>
              <div className="h-[1px] bg-teal-600 w-14 my-3" />
              <div className="text-sm">
                {references.map((r, idx) => (
                  <div key={idx} className="mb-4">
                    <input
                      name="name"
                      value={r.name}
                      readOnly={!isEdit}
                      onChange={(e) => handleRefChange(idx, e)}
                      className="font-semibold bg-transparent outline-none text-sm w-full"
                    />
                    <textarea
                      name="details"
                      value={r.details}
                      readOnly={!isEdit}
                      onChange={(e) => handleRefChange(idx, e)}
                      rows={2}
                      className="text-xs bg-transparent outline-none mt-1 resize-none w-full"
                    />
                    {isEdit && (
                      <button
                        onClick={() => removeReference(idx)}
                        className="text-red-500 text-xs mt-1"
                      >
                        <FaTrash /> Remove
                      </button>
                    )}
                  </div>
                ))}
                {isEdit && (
                  <button
                    onClick={addReference}
                    className="flex items-center gap-2 text-sm text-teal-700 mt-1"
                  >
                    <FaPlus /> Add Reference
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT column - white */}
          <div className="w-7/12 bg-white p-8">
            {/* About */}
            <div>
              <div className="flex items-center justify-between">
                <h3 className="text-teal-700 font-semibold text-lg">
                  About Me
                </h3>
                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                  i
                </div>
              </div>
              <div className="h-[1px] bg-gray-200 my-3" />
              <textarea
                value={formData.about}
                onChange={handleChange}
                name="about"
                readOnly={!isEdit}
                rows={4}
                className="w-full bg-transparent outline-none text-sm resize-none"
              />
            </div>

            {/* Job Experience */}
            <div className="mt-6">
              <div className="flex items-center justify-between">
                <h3 className="text-teal-700 font-semibold text-lg">
                  Job Experience
                </h3>
                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                  ★
                </div>
              </div>
              <div className="h-[1px] bg-gray-200 my-3" />
              <div>
                {experiences.map((exp, idx) => (
  <div key={idx} className="mb-6">

    {/* Title + Company + Period */}
    <div className="flex justify-between gap-4">
      <div className="flex-1">
        <input
          name="title"
          value={exp.title}
          onChange={(e) => handleExpChange(idx, e)}
          readOnly={!isEdit}
          className="font-semibold text-sm bg-transparent outline-none block w-full"
        />
        <input
          name="company"
          value={exp.company}
          onChange={(e) => handleExpChange(idx, e)}
          readOnly={!isEdit}
          className="text-xs bg-transparent outline-none mt-1 block w-full"
        />
      </div>

      <input
        name="period"
        value={exp.period}
        onChange={(e) => handleExpChange(idx, e)}
        readOnly={!isEdit}
        className="text-xs text-gray-500 bg-transparent outline-none text-right w-32"
      />
    </div>

    {/* Description */}
    <textarea
      name="description"
      value={exp.description}
      onChange={(e) => {
        handleExpChange(idx, e);
        e.target.style.height = "auto";
        e.target.style.height = e.target.scrollHeight + "px";
      }}
      readOnly={!isEdit}
      className="w-full bg-transparent outline-none text-sm mt-2 resize-none overflow-hidden"
    />

    {isEdit && (
      <div className="mt-2">
        <button
          onClick={() => removeExperience(idx)}
          className="text-red-500 text-xs"
        >
          <FaTrash /> Remove
        </button>
      </div>
    )}
  </div>
))}
{isEdit && (
  <button
    onClick={addExperience}
    className="flex items-center gap-2 text-sm text-teal-700"
  >
    <FaPlus /> Add Experience
  </button>
)}

              </div>
            </div>

            {/* Skills */}
            <div className="mt-6">
              <div className="flex items-center justify-between">
                <h3 className="text-teal-700 font-semibold text-lg">
                  Skills
                </h3>
                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                  •
                </div>
              </div>
              <div className="h-[1px] bg-gray-200 my-3" />
              <div className="grid grid-cols-2 gap-4">
                {skills.map((s, idx) => (
                  <div key={idx}>
                    <div className="flex items-center justify-between">
                      <input
                        name="title"
                        value={s.title}
                        readOnly={!isEdit}
                        onChange={(e) => handleSkillChange(idx, e)}
                        className="bg-transparent outline-none text-sm"
                      />
                      {isEdit && (
                        <input
                          name="percent"
                          value={s.percent}
                          onChange={(e) => handleSkillChange(idx, e)}
                          type="number"
                          className="w-16 bg-transparent outline-none text-sm"
                          min={0}
                          max={100}
                        />
                      )}
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full mt-2">
                      <div
                        className="h-full rounded-full bg-teal-500"
                        style={{
                          width: `${s.percent}%`,
                          transition: "width 0.3s",
                        }}
                      />
                    </div>
                    {isEdit && (
                      <div className="mt-1">
                        <button
                          onClick={() => removeSkill(idx)}
                          className="text-red-500 text-xs"
                        >
                          <FaTrash /> Remove
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {isEdit && (
                <div className="mt-3">
                  <button
                    onClick={addSkill}
                    className="flex items-center gap-2 text-sm text-teal-700"
                  >
                    <FaPlus /> Add Skill
                  </button>
                </div>
              )}
            </div>

            {/* Language & Hobbies */}
            <div className="mt-6 grid grid-cols-2 gap-6">
              <div>
                <h4 className="text-teal-700 font-semibold">
                  Language
                </h4>
                <div className="h-[1px] bg-gray-200 my-3" />
                <ul className="text-sm space-y-2">
                  {languages.map((l, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-2"
                    >
                      <div className="w-2 h-2 rounded-full bg-teal-600" />
                      {isEdit ? (
                        <input
                          value={l}
                          onChange={(e) =>
                            handleLanguageChange(i, e)
                          }
                          className="bg-transparent outline-none text-sm"
                        />
                      ) : (
                        <span>{l}</span>
                      )}
                      {isEdit && (
                        <button
                          onClick={() => removeLanguage(i)}
                          className="text-red-500 text-xs"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
                {isEdit && (
                  <button
                    onClick={addLanguage}
                    className="flex items-center gap-2 text-sm text-teal-700 mt-2"
                  >
                    <FaPlus /> Add Language
                  </button>
                )}
              </div>

              <div>
                <h4 className="text-teal-700 font-semibold">
                  Hobbies
                </h4>
                <div className="h-[1px] bg-gray-200 my-3" />
                <ul className="text-sm space-y-2">
                  {hobbies.map((h, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-2"
                    >
                      <div className="w-2 h-2 rounded-full bg-teal-600" />
                      {isEdit ? (
                        <input
                          value={h}
                          onChange={(e) =>
                            handleHobbyChange(i, e)
                          }
                          className="bg-transparent outline-none text-sm"
                        />
                      ) : (
                        <span>{h}</span>
                      )}
                      {isEdit && (
                        <button
                          onClick={() => removeHobby(i)}
                          className="text-red-500 text-xs"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
                {isEdit && (
                  <button
                    onClick={addHobby}
                    className="flex items-center gap-2 text-sm text-teal-700 mt-2"
                  >
                    <FaPlus /> Add Hobby
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Template2;
